import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { listLegalDocumentsAdmin } from "@/lib/api/legal.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { legalDocumentPaths } from "@/lib/legal/defaults";
import { TableRowActions, tableActionsColumn } from "@/components/admin/TableRowActions";
import type { LegalDocumentSlug } from "@/lib/legal/defaults";

export const Route = createFileRoute("/admin/legal/")({
  loader: () => listLegalDocumentsAdmin(),
  component: AdminLegalListPage,
});

type Row = Awaited<ReturnType<typeof listLegalDocumentsAdmin>>[number];

const columns: ColumnDef<Row>[] = [
  {
    accessorKey: "label",
    header: "Document",
    cell: ({ row }) => <span className="font-medium">{row.original.label}</span>,
  },
  {
    accessorKey: "currentVersion",
    header: "Version",
    cell: ({ row }) => (row.original.currentVersion ? `v${row.original.currentVersion}` : "—"),
  },
  {
    accessorKey: "lastPublishedAt",
    header: "Last published",
    cell: ({ row }) =>
      row.original.lastPublishedAt ? new Date(row.original.lastPublishedAt).toLocaleString() : "Not published",
  },
  {
    accessorKey: "hasDraftChanges",
    header: "Draft",
    cell: ({ row }) => (row.original.hasDraftChanges ? "Unpublished changes" : "—"),
  },
  {
    accessorKey: "slug",
    header: "Public URL",
    cell: ({ row }) => (
      <a
        href={legalDocumentPaths[row.original.slug as LegalDocumentSlug]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline"
      >
        {legalDocumentPaths[row.original.slug as LegalDocumentSlug]}
      </a>
    ),
  },
  {
    ...tableActionsColumn<Row>(),
    cell: ({ row }) => (
      <TableRowActions
        edit={{ to: "/admin/legal/$slug", params: { slug: row.original.slug } }}
        showDelete={false}
      />
    ),
  },
];

function AdminLegalListPage() {
  const data = Route.useLoaderData();

  return (
    <AdminPageShell>
      <AdminPageHeader title="Legal documents" />
      <p className="mb-4 text-sm text-muted-foreground">
        Edit Privacy Policy and Terms of Use. Each publish creates a stored version you can review or restore.
      </p>
      <DataTable tableId="legal-documents" columns={columns} data={data} />
    </AdminPageShell>
  );
}
